import * as Yup from 'yup';
import { useState } from 'react';
import { useSnackbar } from 'notistack';
import { useFormik, Form, FormikProvider } from 'formik';
import { Icon } from '@iconify/react';
import eyeFill from '@iconify/icons-eva/eye-fill';
import closeFill from '@iconify/icons-eva/close-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';
import { Stack, Alert, TextField, Checkbox, FormControlLabel, Link,IconButton,InputAdornment } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import axios from '../../../utils/axios';
import useAuth from '../../../hooks/useAuth';

// ----------------------------------------------------------------------

export default function LoginForm() {
  const { login, loginWithToken } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
 const [showPassword, setShowPassword] = useState(false);

  const [step, setStep] = useState('login'); // login | otp
  const [otpUsername, setOtpUsername] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
 const [isVerifying, setIsVerifying] = useState(false);
  const LoginSchema = Yup.object().shape({
    username: Yup.string().required('Requis'),
    password: Yup.string().required('Requis')
  });

   const handleShowPassword = () => {
    setShowPassword((show) => !show);
  };
  const formik = useFormik({
    initialValues: {
      username: '',
      password: '',
      remember: false
    },
    validationSchema: LoginSchema,

    onSubmit: async (values, { setSubmitting, setErrors }) => {
      try {
        const res = await login(values.username, values.password, values.remember);

        if (res?.otp_required) {
          setOtpUsername(res.username);
          setStep('otp');
          enqueueSnackbar('Code OTP envoyé par email', { variant: 'info' });
          setSubmitting(false);
          return;
        }

        enqueueSnackbar('Connexion réussie', { variant: 'success' });
        setSubmitting(false);
      } catch {
        setErrors({ afterSubmit: 'Identifiants incorrects' });
        setSubmitting(false);
      }
    }
  });

  const handleVerifyOTP = async () => {
    try {
       setIsVerifying(true);
      const res = await axios.post('/api/auth/validate-otp/', {
        username: otpUsername,
        otp_code: otpCode
      });

      await loginWithToken(
        res.data.accessToken,
        res.data.role,
        res.data.user
      );

      enqueueSnackbar('Connexion sécurisée réussie', { variant: 'success' });
    } catch (err) {
      console.error('errorrrrrrrrrrr',err.response);
      enqueueSnackbar(err?.error || 'OTP invalide', {
        variant: 'error'
      });
    }finally {
    setIsVerifying(false);
  }
  };

  const handleResendOTP = async () => {
  // Désactiver le bouton pendant la requête
  setResendLoading(true);
  
  try {
    const response = await axios.post('/api/auth/resend-otp/', {
      username: otpUsername
    });
    
    // Messages de succès
    const successMessages = [
      'Nouveau code de vérification envoyé',
      'Un code de vérification valide existe déjà',
      'Si votre compte existe, un nouveau code vous a été envoyé'
    ];
    
    // Vérifier le type de message
    if (response.data.success === false) {
      // Message d'erreur
      enqueueSnackbar(response.data.message || 'Erreur lors du renvoi du code', { 
        variant: 'error',
        autoHideDuration: 3000 
      });
    } else if (successMessages.some(msg => response.data.message?.includes(msg))) {
      // Message de succès
      enqueueSnackbar(response.data.message || 'Code envoyé avec succès', { 
        variant: 'success',
        autoHideDuration: 2000 
      });
    } else {
      // Message info par défaut
      enqueueSnackbar(response.data.message || 'Nouveau code envoyé', { 
        variant: 'info',
        autoHideDuration: 2000 
      });
    }
    
  } catch (error) {
    // Gestion des erreurs
    let errorMessage = 'Erreur lors du renvoi du code';
    
    if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.response?.data?.error) {
      errorMessage = error.response.data.error;
    } else if (error.message === 'Network Error') {
      errorMessage = 'Erreur de connexion. Vérifiez votre internet.';
    }
    
    enqueueSnackbar(errorMessage, { 
      variant: 'error',
      autoHideDuration: 4000 
    });
    
  } finally {
    // Réactiver le bouton
    setResendLoading(false);
  }
};

  return (
    <FormikProvider value={formik}>
      {step === 'login' && (
        <Form autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
          <Stack spacing={3}>
            {formik.errors.afterSubmit && (
              <Alert severity="error">{formik.errors.afterSubmit}</Alert>
            )}

            <TextField
              fullWidth
              label="Username"
              {...formik.getFieldProps('username')}
            />

            <TextField
            fullWidth
            autoComplete="current-password"
            type={showPassword ? 'text' : 'password'}
            label="Mot de passe"
            {...formik.getFieldProps('password')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleShowPassword} edge="end">
                    <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                  </IconButton>
                </InputAdornment>
              )
            }}
            // helperText={touched.password && errors.password}
          />

            <FormControlLabel
              control={<Checkbox {...formik.getFieldProps('remember')} />}
              label="Se souvenir de moi"
            />

            <LoadingButton
              fullWidth
              size="large"
              type="submit"
              variant="contained"
              loading={formik.isSubmitting}
            >
              Se connecter
            </LoadingButton>
          </Stack>
        </Form>
      )}

      {step === 'otp' && (
        <Stack spacing={3}>
          <Alert severity="info">
            Entrez le code OTP envoyé par email
          </Alert>

          <TextField
            fullWidth
            label="Code OTP"
            value={otpCode}
            onChange={(e) => setOtpCode(e.target.value)}
          />

          <LoadingButton
            fullWidth
            variant="contained"
            onClick={handleVerifyOTP}
             loading={isVerifying}
             loadingPosition='center'
          >
            Vérifier le code
          </LoadingButton>

          <Link component="button" onClick={handleResendOTP}>
            Renvoyer le code
          </Link>

          <Link component="button" onClick={() => setStep('login')}>
            Retour
          </Link>
        </Stack>
      )}
    </FormikProvider>
  );
}
